import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Heart, Trash2, Pencil, Sparkles, MoreHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/lib/supabase";
import { AppShell } from "@/components/app-shell";
import { ImageBox, SectionLabel } from "@/components/ui-extras";
import { EmptyState } from "@/components/empty-state";
import { HintBanner } from "@/components/hint-banner";
import { StarRating } from "@/components/star-rating";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { LookEditDialog } from "@/components/look-edit-dialog";
import { useItemLimits, FREE_LIMITS } from "@/lib/useSubscription";
import { UpgradePromptSheet } from "@/components/upgrade-prompt-sheet";
import { useNavigate } from "@tanstack/react-router";
import {
  CollectionToolbar,
  applyTextSearch,
  type ActiveFilters,
  type FilterFieldDef,
  type SortOption,
} from "@/components/collection-toolbar";
import { OCCASION_PRESETS, FEELING_PRESETS } from "@/components/smart-select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { BG_PREFERENCE_SELECT, bgFields, resolvePreferredPhoto } from "@/lib/photo-preference";

type LookKind = "jewelry" | "clothing" | "accessory";
type LookTile = {
  id: string;
  name: string;
  photo_url: string | null;
  kind: LookKind;
  photo_url_no_bg: string | null;
  prefer_clean_bg: boolean | null;
  clean_bg_color: string | null;
};
type LookWithItems = {
  id: string;
  name: string;
  occasion: string | null;
  rating: number | null;
  look_photo_url: string | null;
  is_favorite: boolean | null;
  feelings: string | null;
  styling_notes: string | null;
  created_at: string;
  tiles: LookTile[];
  clothing_count: number;
  jewelry_count: number;
  accessory_count: number;
};

export const Route = createFileRoute("/looks/")({
  head: () => ({
    meta: [
      { title: "Looks — Cailève" },
      { name: "description", content: "Your saved editorial looks." },
    ],
  }),
  component: () => (
    <AppShell>
      <LooksPage />
    </AppShell>
  ),
});

function LooksPage() {
  const { user } = useAuth();
  if (!user) return null;
  const uid = user.id;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const limits = useItemLimits();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const looksUpgradeReason: "item_limit" | "item_limit_lapsed" =
    (limits.counts?.looks ?? 0) > FREE_LIMITS.looks
      ? "item_limit_lapsed"
      : "item_limit";
  const [pendingDelete, setPendingDelete] = useState<LookWithItems | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<ActiveFilters>({});
  const [sortValue, setSortValue] = useState<string>("recent");
  const [quickFilter, setQuickFilter] = useState<"all" | "favorites" | "recent">("all");

  const { data, isLoading } = useQuery({
    queryKey: ["saved_looks", uid],
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("saved_looks")
        .select(
          `id, name, occasion, rating, look_photo_url, is_favorite, feelings, styling_notes, created_at,
           saved_look_items (
             id, item_type, item_id, clothing_item_id, jewelry_item_id, position
           )`
        )
        .eq("user_id", uid)
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message || "Network error");
      type Row = {
        id: string;
        name: string;
        occasion: string | null;
        rating: number | null;
        look_photo_url: string | null;
        is_favorite: boolean | null;
        feelings: string | null;
        styling_notes: string | null;
        created_at: string;
        saved_look_items: {
          id: string;
          item_type: string | null;
          item_id: string | null;
          clothing_item_id: string | null;
          jewelry_item_id: string | null;
          position: number | null;
        }[];
      };
      const rows = (data ?? []) as unknown as Row[];

      // Collect all referenced item ids across all looks.
      const allClothingIds = new Set<string>();
      const allJewelryishIds = new Set<string>();
      const perLookResolved: Record<string, { id: string; kind: LookKind; position: number }[]> = {};
      for (const r of rows) {
        const list: { id: string; kind: LookKind; position: number }[] = [];
        for (const sli of r.saved_look_items) {
          const itemId = sli.item_id ?? sli.clothing_item_id ?? sli.jewelry_item_id;
          if (!itemId) continue;
          let kind: LookKind | null = null;
          if (sli.item_type === "clothing" || sli.item_type === "jewelry" || sli.item_type === "accessory") {
            kind = sli.item_type as LookKind;
          } else if (sli.clothing_item_id) {
            kind = "clothing";
          } else if (sli.jewelry_item_id) {
            kind = "jewelry";
          }
          if (!kind) continue;
          if (kind === "clothing") allClothingIds.add(itemId);
          else allJewelryishIds.add(itemId);
          list.push({ id: itemId, kind, position: sli.position ?? 0 });
        }
        list.sort((a, b) => a.position - b.position);
        perLookResolved[r.id] = list;
      }

      const [{ data: cRows }, { data: jRows }] = await Promise.all([
        allClothingIds.size
          ? supabase
              .from("clothing_items")
              .select(`id, name, photo_url, ${BG_PREFERENCE_SELECT}`)
              .in("id", Array.from(allClothingIds))
          : Promise.resolve({ data: [] as Array<{ id: string; name: string; photo_url: string | null; photo_url_no_bg: string | null; prefer_clean_bg: boolean | null; clean_bg_color: string | null }> }),
        allJewelryishIds.size
          ? supabase
              .from("jewelry_items")
              .select(`id, name, photo_url, is_accessory, ${BG_PREFERENCE_SELECT}`)
              .in("id", Array.from(allJewelryishIds))
          : Promise.resolve({ data: [] as Array<{ id: string; name: string; photo_url: string | null; is_accessory: boolean | null; photo_url_no_bg: string | null; prefer_clean_bg: boolean | null; clean_bg_color: string | null }> }),
      ]);
      const cMap = new Map((cRows ?? []).map((r) => [r.id, r]));
      const jMap = new Map((jRows ?? []).map((r) => [r.id, r]));

      return rows.map((r): LookWithItems => {
        const resolved = perLookResolved[r.id] ?? [];
        const tiles: LookTile[] = [];
        let cn = 0,
          jn = 0,
          an = 0;
        for (const x of resolved) {
          if (x.kind === "clothing") {
            const m = cMap.get(x.id);
            if (m) {
              cn += 1;
              tiles.push({ id: m.id, name: m.name, photo_url: m.photo_url, kind: "clothing", ...bgFields(m) });
            }
          } else {
            const m = jMap.get(x.id);
            if (m) {
              const kind: LookKind = x.kind === "accessory" || m.is_accessory ? "accessory" : "jewelry";
              if (kind === "accessory") an += 1;
              else jn += 1;
              tiles.push({ id: m.id, name: m.name, photo_url: m.photo_url, kind, ...bgFields(m) });
            }
          }
        }
        return {
          id: r.id,
          name: r.name,
          occasion: r.occasion,
          rating: r.rating,
          look_photo_url: r.look_photo_url,
          is_favorite: r.is_favorite,
          feelings: r.feelings,
          styling_notes: r.styling_notes,
          created_at: r.created_at,
          tiles,
          clothing_count: cn,
          jewelry_count: jn,
          accessory_count: an,
        };
      });
    },
  });

  const filterFields: FilterFieldDef[] = useMemo(
    () => [
      { key: "occasion", label: "Occasion", type: "select", options: [...OCCASION_PRESETS] },
      { key: "rating", label: "Rating", type: "select", options: ["5","4","3","2","1"] },
      { key: "feeling", label: "Feeling", type: "select", options: [...FEELING_PRESETS] },
      { key: "is_favorite", label: "Favorites only", type: "toggle" },
    ],
    [],
  );

  const sortOptions: SortOption[] = [
    { value: "recent", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "name_asc", label: "Name A → Z" },
    { value: "name_desc", label: "Name Z → A" },
    { value: "rating_desc", label: "Highest Rated" },
    { value: "rating_asc", label: "Lowest Rated" },
    { value: "favorites", label: "Favorites First" },
  ];

  const displayLooks = useMemo(() => {
    let items = data ?? [];
    if (filters.is_favorite) items = items.filter((l) => l.is_favorite);
    if (typeof filters.occasion === "string" && filters.occasion)
      items = items.filter((l) => l.occasion === filters.occasion);
    if (typeof filters.rating === "string" && filters.rating)
      items = items.filter((l) => (l.rating ?? 0) === Number(filters.rating));
    if (typeof filters.feeling === "string" && filters.feeling) {
      const f = (filters.feeling as string).toLowerCase();
      items = items.filter((l) =>
        (l.feelings ?? "").toLowerCase().split(",").map((s) => s.trim()).includes(f),
      );
    }
    items = applyTextSearch(items, searchQuery, ["name", "occasion", "styling_notes"]);
    const sorted = [...items];
    const cmp = (a?: string | null, b?: string | null) => (a ?? "").localeCompare(b ?? "");
    switch (sortValue) {
      case "oldest": sorted.sort((a, b) => (a.created_at ?? "").localeCompare(b.created_at ?? "")); break;
      case "name_asc": sorted.sort((a, b) => cmp(a.name, b.name)); break;
      case "name_desc": sorted.sort((a, b) => cmp(b.name, a.name)); break;
      case "rating_desc": sorted.sort((a, b) => (b.rating ?? -1) - (a.rating ?? -1)); break;
      case "rating_asc": sorted.sort((a, b) => (a.rating ?? Infinity) - (b.rating ?? Infinity)); break;
      case "favorites": sorted.sort((a, b) => Number(!!b.is_favorite) - Number(!!a.is_favorite)); break;
      default: sorted.sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
    }
    return sorted;
  }, [data, filters, searchQuery, sortValue]);

  const hasActiveFilters =
    !!searchQuery ||
    Object.values(filters).some((v) => v !== undefined && v !== "" && v !== false);

  const toggleFavorite = useMutation({
    mutationFn: async (l: LookWithItems) => {
      const { error } = await supabase
        .from("saved_looks")
        .update({ is_favorite: !l.is_favorite } as never)
        .eq("id", l.id);
      if (error) throw new Error(error.message || "Network error");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["saved_looks", uid] });
      qc.invalidateQueries({ queryKey: ["saved_looks_favorites", uid] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteLook = useMutation({
    mutationFn: async (id: string) => {
      const { error: itemsErr } = await supabase
        .from("saved_look_items")
        .delete()
        .eq("look_id", id);
      if (itemsErr) throw itemsErr;
      const { error } = await supabase.from("saved_looks").delete().eq("id", id);
      if (error) throw new Error(error.message || "Network error");
    },
    onSuccess: () => {
      toast.success("Look removed.");
      setPendingDelete(null);
      qc.invalidateQueries({ queryKey: ["saved_looks", uid] });
      qc.invalidateQueries({ queryKey: ["saved_looks_favorites", uid] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateRating = useMutation({
    mutationFn: async ({ id, rating }: { id: string; rating: number }) => {
      const { error } = await supabase
        .from("saved_looks")
        .update({ rating } as never)
        .eq("id", id);
      if (error) throw new Error(error.message || "Network error");
    },
    onMutate: async ({ id, rating }) => {
      await qc.cancelQueries({ queryKey: ["saved_looks", uid] });
      const previous = qc.getQueryData<LookWithItems[]>(["saved_looks", uid]);
      qc.setQueryData<LookWithItems[]>(["saved_looks", uid], (old) =>
        (old ?? []).map((l) => (l.id === id ? { ...l, rating } : l)),
      );
      return { previous };
    },
    onError: (e: Error, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(["saved_looks", uid], ctx.previous);
      toast.error(e.message);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["saved_looks", uid] });
      qc.invalidateQueries({ queryKey: ["saved_looks_favorites", uid] });
    },
  });

  return (
    <div className="max-w-7xl mx-auto px-8 py-12">
      <div className="flex items-end justify-between border-b border-border pb-6 mb-10">
        <div>
          <h1 className="font-serif-display text-4xl">Your Styled Looks</h1>
          <p className="font-serif-display italic text-base mt-1" style={{ color: "var(--caileve-lilac)" }}>The outfits Cailève has helped you remember.</p>
        </div>
      </div>
      <UpgradePromptSheet open={upgradeOpen} onOpenChange={setUpgradeOpen} reason={looksUpgradeReason} />

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="space-y-3">
              <div className="animate-pulse aspect-square w-full" style={{ backgroundColor: "var(--caileve-soft)" }} />
              <div className="animate-pulse h-5 w-36 rounded-sm" style={{ backgroundColor: "var(--caileve-soft)" }} />
              <div className="animate-pulse h-2.5 w-20 rounded-sm" style={{ backgroundColor: "var(--caileve-soft)" }} />
            </div>
          ))}
        </div>
      ) : !data?.length ? (
        <EmptyState
          icon={Sparkles}
          message="No styled looks yet."
          subMessage="When Cailève creates a look you love, save it here. Style your first look to begin."
        />
      ) : (
        <>
          <HintBanner
            hintKey="hint_looks_3"
            show={(data?.length ?? 0) >= 3}
            message="Your emotional style profile is building. Cailève is learning what makes you feel your best."
          />

          <div className="flex gap-2 mb-6">
            {(
              [
                { key: "all", label: "All Looks" },
                { key: "favorites", label: "Favorites" },
                { key: "recent", label: "Recent" },
              ] as const
            ).map(({ key, label }) => {
              const active = quickFilter === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setQuickFilter(key);
                    if (key === "favorites") {
                      setFilters({ is_favorite: true });
                    } else {
                      setFilters({});
                      setSortValue("recent");
                    }
                  }}
                  className="px-4 text-sm transition-colors"
                  style={
                    active
                      ? {
                          backgroundColor: "transparent",
                          color: "var(--caileve-ink)",
                          borderBottom: "2px solid var(--caileve-gold)",
                          borderRadius: 0,
                          fontWeight: 600,
                          paddingBottom: 6,
                          border: "none",
                          borderBottomStyle: "solid" as const,
                          borderBottomWidth: 2,
                          borderBottomColor: "var(--caileve-gold)",
                          cursor: "pointer",
                        }
                      : {
                          backgroundColor: "transparent",
                          color: "var(--caileve-muted)",
                          border: "none",
                          paddingBottom: 6,
                          cursor: "pointer",
                        }
                  }
                >
                  {label}
                </button>
              );
            })}
          </div>

          <CollectionToolbar
            searchPlaceholder="Search your looks…"
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            filterFields={filterFields}
            activeFilters={filters}
            onFilterChange={(k, v) => setFilters((p) => ({ ...p, [k]: v }))}
            onClearFilters={() => setFilters({})}
            sortOptions={sortOptions}
            sortValue={sortValue}
            onSortChange={setSortValue}
          />
          {hasActiveFilters && (
            <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--caileve-lilac-text)] mb-6">
              {displayLooks.length} {displayLooks.length === 1 ? "look" : "looks"}
            </p>
          )}
          {displayLooks.length === 0 ? (
            <div className="border border-dashed border-[var(--caileve-soft)] py-24 text-center">
              <p
                className="font-serif-display text-2xl"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-lilac)" }}
              >
                No looks match your search.
              </p>
              <button
                type="button"
                onClick={() => { setSearchQuery(""); setFilters({}); }}
                className="mt-6 px-6 py-2 text-xs uppercase tracking-[0.25em] text-[var(--caileve-bg)]"
                style={{ backgroundColor: "var(--caileve-gold)", borderRadius: "var(--caileve-radius-pill)" }}
              >
                Clear Filters
              </button>
            </div>
          ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayLooks.map((l) => {
            const tiles = l.tiles.slice(0, 4);
            return (
              <article key={l.id} className="group relative" onClick={() => setOpenMenuId(null)} style={{ border: "1px solid var(--caileve-gold-12)", boxShadow: "var(--caileve-shadow-card)", borderRadius: "var(--caileve-radius-md)", overflow: "hidden" }}>
                <div className="absolute top-3 right-3 z-20 flex items-center gap-1">
                  <button
                    type="button"
                    aria-label={l.is_favorite ? "Unfavorite look" : "Favorite look"}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleFavorite.mutate(l);
                    }}
                    className="h-8 w-8 flex items-center justify-center bg-background/80 backdrop-blur-sm border border-border hover:bg-background transition-colors"
                  >
                    <Heart
                      className={cn(
                        "h-4 w-4",
                        l.is_favorite ? "fill-[var(--caileve-copper)] text-[var(--caileve-copper)]" : "text-[var(--caileve-copper)]"
                      )}
                    />
                  </button>
                  <div style={{ position: "relative" }}>
                    <button
                      type="button"
                      aria-label="More options"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === l.id ? null : l.id);
                      }}
                      className="h-8 w-8 flex items-center justify-center bg-background/80 backdrop-blur-sm border border-border text-[var(--caileve-muted)] hover:bg-background transition-colors"
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                    {openMenuId === l.id && (
                      <div
                        style={{
                          position: "absolute",
                          top: "calc(100% + 4px)",
                          right: 0,
                          backgroundColor: "var(--caileve-bg)",
                          border: "1px solid var(--caileve-soft)",
                          borderRadius: 8,
                          boxShadow: "var(--caileve-shadow-elevated)",
                          zIndex: 50,
                          minWidth: 120,
                          overflow: "hidden",
                        }}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpenMenuId(null);
                            setEditId(l.id);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            width: "100%",
                            padding: "10px 14px",
                            fontFamily: "Inter, sans-serif",
                            fontSize: 13,
                            color: "var(--caileve-ink)",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            textAlign: "left" as const,
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpenMenuId(null);
                            setPendingDelete(l);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            width: "100%",
                            padding: "10px 14px",
                            fontFamily: "Inter, sans-serif",
                            fontSize: 13,
                            color: "var(--caileve-error, #C0392B)",
                            background: "none",
                            border: "none",
                            borderTop: "1px solid var(--caileve-soft)",
                            cursor: "pointer",
                            textAlign: "left" as const,
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {l.is_favorite && (
                  <div className="absolute top-3 left-3 z-20 h-8 w-8 flex items-center justify-center bg-background/80 backdrop-blur-sm border border-border pointer-events-none">
                    <Heart className="h-4 w-4 fill-[var(--caileve-copper)] text-[var(--caileve-copper)]" />
                  </div>
                )}
                <Link
                  to="/looks/$id"
                  params={{ id: l.id }}
                  className="block"
                >
                {l.look_photo_url ? (
                  <div className="aspect-[4/5] relative overflow-hidden" style={{ borderRadius: 14 }}>
                    <ImageBox
                      src={l.look_photo_url}
                      alt={l.name}
                      aspect="aspect-[4/5]"
                    />
                    {tiles.length > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: 8,
                          right: 8,
                          display: "flex",
                          gap: 4,
                        }}
                      >
                        {tiles.slice(0, 2).map((t) => (
                          <div
                            key={`${t.kind}-${t.id}`}
                            style={{
                              width: 44,
                              height: 44,
                              backgroundColor: "#fff",
                              padding: 2,
                              boxShadow: "0 1px 6px rgba(0,0,0,0.2)",
                              flexShrink: 0,
                              borderRadius: 6,
                              overflow: "hidden",
                            }}
                          >
                            {(() => {
                              const r = resolvePreferredPhoto(t);
                              return (
                                <ImageBox
                                  src={r.src}
                                  alt={t.name}
                                  aspect="aspect-square"
                                  style={r.backdropStyle}
                                />
                              );
                            })()}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : tiles.length ? (
                  <div className="aspect-[4/5] grid grid-cols-2 gap-1 bg-muted overflow-hidden" style={{ borderRadius: 14 }}>
                    {tiles.map((t, idx) => (
                      <div
                        key={`${t.kind}-${t.id}`}
                        className={cn(
                          "overflow-hidden",
                          tiles.length === 1
                            ? "col-span-2 row-span-2"
                            : tiles.length === 2
                              ? "col-span-1 row-span-2"
                              : tiles.length === 3 && idx === 0
                                ? "col-span-2"
                                : ""
                        )}
                      >
                        {(() => {
                          const r = resolvePreferredPhoto(t);
                          return (
                            <ImageBox src={r.src} alt={t.name} aspect="h-full w-full" style={r.backdropStyle} />
                          );
                        })()}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className="aspect-[4/5] flex items-center justify-center"
                    style={{ backgroundColor: "var(--caileve-soft)", borderRadius: 14 }}
                  >
                    <p className="font-serif-display italic text-lg text-[var(--caileve-lilac)]">
                      A composed look.
                    </p>
                  </div>
                )}
                <div className="flex items-start justify-between mt-4 gap-3 pb-5">
                  <div>
                    <p className="font-serif-display text-2xl leading-tight">{l.name}</p>
                    {(l.clothing_count + l.jewelry_count + l.accessory_count) > 0 && (() => {
                      const total = l.clothing_count + l.jewelry_count + l.accessory_count;
                      const isJewelryLed = l.jewelry_count > l.clothing_count && l.jewelry_count > 0;
                      const occasionPrefix = l.occasion ? `${l.occasion} · ` : "";
                      const pieceLabel = isJewelryLed ? "Jewelry-led look" : `${total} ${total === 1 ? "piece" : "pieces"}`;
                      return (
                        <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-2">
                          {occasionPrefix}{pieceLabel}
                        </p>
                      );
                    })()}
                    {l.feelings && l.feelings.trim().length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {l.feelings
                          .split(",")
                          .map((f) => f.trim())
                          .filter(Boolean)
                          .slice(0, 3)
                          .map((f) => (
                            <span
                              key={f}
                            className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em]"
                            style={{
                              backgroundColor: "var(--caileve-soft)",
                              color: "var(--caileve-ink)",
                              fontFamily: "Inter, sans-serif",
                            }}
                            >
                              {f}
                            </span>
                          ))}
                      </div>
                    )}
                  </div>
                  <StarRating
                    value={l.rating ?? 0}
                    size="sm"
                    onChange={(next) => updateRating.mutate({ id: l.id, rating: next })}
                  />
                </div>
                </Link>
              </article>
            );
          })}
        </div>
          )}
        </>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this look?</AlertDialogTitle>
            <AlertDialogDescription>
              "{pendingDelete?.name}" will be permanently removed from your lookbook. The pieces themselves will remain in your collection.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLook.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteLook.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (pendingDelete) deleteLook.mutate(pendingDelete.id);
              }}
            >
              {deleteLook.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editId} onOpenChange={(o) => !o && setEditId(null)}>
        {editId && (
          <LookEditDialog
            lookId={editId}
            userId={uid}
            onDone={() => setEditId(null)}
          />
        )}
      </Dialog>
    </div>
  );
}